import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ChangeEntry, ChangeKind } from '@app/sharedModule/change-review/change-review.model';
import { isEmptyValue, matchesPattern, moneyFormatter } from '@app/sharedModule/change-review/change-review.utils';
import { revertAt } from '@app/sharedModule/change-review/form-revert';
import { ruleGroupPosition } from '@app/sharedModule/change-review/rule-groups';
import type { AdditionalPriceRanges, Shippings } from '@pages/checkout-app/types';
import type { CheckoutAppComponent } from './checkout.app.component';
import { describePriceTier, NO_SECTION, shippingRuleGroups } from './shipping-method-review.config';
import { AREA_LIST_FIELDS, AreaListField, PriceTier, SavedCondition } from './shipping-method-review.model';

const NOT_REVERTABLE = [
  // switching type rebuilds the whole form
  'shippingMethodCategory',
  'carrierId',
  'deliveryDaysAndTimes',
  'deliveryDaysAndTimesHolidays',
  'deliveryPromiseDontCalculateOn',
  'deliveryAreaSelectMethod',
  'geoDeliveryArea',
  'geoDeliveryAreaMethod',
  'shippedFromLocation',
  'estimatedTimeConversions',
  'deliveryPromisePdqIqBaseShippingMethod',
  'discountType',
  'additionalPriceRanges[].discountType',
  'international.taxPaymentType',
  'international.countriesAndRegions',
  // the carrier toggles keep their own copy of these lists
  'shipmentExtras.activeServiceProviders',
  'shipmentExtras.activeProviderServiceLevelsIds',
];

export type RevertHost = Pick<
  CheckoutAppComponent,
  | 'formGroup'
  | 'shipmentExtrasFormGroup'
  | 'listedStatesControl'
  | 'additionalPriceRanges'
  | 'conditionsField'
  | 'shippingRulesEditor'
  | 'zoneOptions'
  | 'zonesPlaceholder'
  | 'zonesOriginAddress'
  | AreaListField
  | 'setField'
  | 'onZipCodesOperatorChange'
  | 'onZipCodeTextareaFocusOut'
  | 'onZonesChange'
  | 'setZonesOriginAddress'
  | 'prepareConditionsToSave'
  | 'setDeliveryDaysAndTimes'
>;

export interface RevertContext {
  describeRule: (rule: SavedCondition, money: Money) => string;
  sectionNames: () => Record<string, string>;
  setCurrency: (currency: string) => void;
}

type Money = (value: unknown) => string;

const formBuilder = new UntypedFormBuilder();

export function isEmptyConditionRow(control: AbstractControl): boolean {
  return Object.values(control.value).every((value) => !value);
}

// empty groups aren't saved, so they mustn't shift group numbers
export function isFilledConditionGroup(control: AbstractControl): control is UntypedFormArray {
  return control instanceof UntypedFormArray && control.controls.some((row) => !isEmptyConditionRow(row));
}

export function buildPriceTierControl(tier: AdditionalPriceRanges): UntypedFormGroup {
  return formBuilder.group({
    minOrderPriceRange: new UntypedFormControl(tier.minOrderPriceRange, [Validators.min(0)]),
    maxOrderPriceRange: new UntypedFormControl(tier.maxOrderPriceRange),
    rate: new UntypedFormControl(tier.rate, [Validators.required, Validators.min(0)]),
    hasDiscount: new UntypedFormControl(tier.hasDiscount ?? false),
    discountType: new UntypedFormControl(tier.discountType ?? 'fixed'),
    discountAmount: new UntypedFormControl(tier.discountAmount ?? null, [Validators.min(0)]),
  });
}

export function buildConditionControl(condition: SavedCondition): UntypedFormGroup {
  const [value1, value2] = (condition.values ?? '').split('||');
  return formBuilder.group({
    name: [condition.name, Validators.required],
    displayName: [condition.displayName, Validators.required],
    condition: [condition.condition, Validators.required],
    values: condition.values,
    weightUnit: condition?.weightUnit,
    value1: [value1, Validators.required],
    value2: value2 ?? null,
    stringParam: null,
    optionalValue: condition?.optionalValue,
  });
}

export class ShippingMethodRevert {
  constructor(
    private host: RevertHost,
    private context: RevertContext,
  ) {}

  // baseline: what the review compares against
  resolve(entry: ChangeEntry, baseline: Partial<Shippings> = {}): (() => void) | null {
    if (NOT_REVERTABLE.some((pattern) => matchesPattern(entry.pattern, pattern))) return null;

    // the review writes money in each side's currency, so matching sentences must too
    const currencies = {
      before: baseline.methodCurrency,
      after: () => this.host.formGroup.get('methodCurrency')?.value,
    };
    const money = moneyFormatter(currencies);
    const sides = {
      before: (value: unknown) => money(value, 'before'),
      after: (value: unknown) => money(value, 'after'),
    };

    const tokens = entry.segments;
    switch (tokens[0]) {
      // rate symbols read the currency from CheckoutService, not from the control
      case 'methodCurrency':
        return () => {
          this.host.setField('methodCurrency', entry.before ?? null);
          this.context.setCurrency(entry.before as string);
        };
      case 'GEODeliveryAreaZipCodesOperator':
        return () => this.host.onZipCodesOperatorChange(entry.before as string);
      case 'conditions':
        return this.resolveRule(entry, tokens[1], baseline.conditions ?? [], sides);
      case 'additionalPriceRanges':
        return this.resolvePriceTier(entry, baseline.additionalPriceRanges ?? [], sides);
      case 'polygonInputValues':
        return this.resolveArea(entry, tokens);
      // switching modes resets the schedule, so the saved one comes back with the mode
      case 'deliveryDaysAllWeek':
        return () => {
          this.host.setField('deliveryDaysAllWeek', entry.before ?? null);
          this.host.setDeliveryDaysAndTimes(baseline.deliveryDaysAndTimes ?? []);
        };
      case 'deliveryMethodSectionId':
        return () => this.host.setField('deliveryMethodSectionId', this.sectionIdByName(entry.before));
      case 'maxxAiConfiguration':
        return revertAt(this.host.formGroup, tokens.slice(1), entry);
      case 'shipmentExtras':
        return revertAt(this.host.shipmentExtrasFormGroup, tokens.slice(1), entry);
      default:
        return revertAt(this.host.formGroup, tokens, entry);
    }
  }

  // rules are matched by their sentence, groups by position as in shippingRuleGroups()
  private resolveRule(
    entry: ChangeEntry,
    groupKey: string,
    baselineConditions: SavedCondition[],
    money: Record<'before' | 'after', Money>,
  ): (() => void) | null {
    // once groups were added or removed, positions no longer point at the same group
    const groupCount = shippingRuleGroups(baselineConditions).length;
    if (groupCount !== shippingRuleGroups(this.host.prepareConditionsToSave()).length) return null;

    const position = ruleGroupPosition(groupKey);
    if (entry.kind === ChangeKind.Removed) {
      const rule = shippingRuleGroups(baselineConditions)[position]?.find(
        (candidate) => this.context.describeRule(candidate, money.before) === entry.before,
      );
      // looked up on run, so a group's put-back rules land together
      return rule ? () => this.addRule(this.filledGroups()[position], rule) : null;
    }
    const findRow = () =>
      this.findRow(position, (rule) => this.context.describeRule(rule, money.after) === entry.after);
    // dropping a group's last rule would drop the group and shift every group after it
    const found = findRow();
    if (entry.kind !== ChangeKind.Added || !found || this.filledRows(found.group).length === 1) return null;
    return () => {
      const found = findRow();
      if (found) this.deleteRule(found.group, found.row);
    };
  }

  private findRow(
    position: number,
    matches: (rule: SavedCondition) => boolean,
  ): { group: UntypedFormArray; row: AbstractControl } | null {
    const group = this.filledGroups()[position];
    // same order as the group's filled rows
    const index = shippingRuleGroups(this.host.prepareConditionsToSave())[position]?.findIndex(matches) ?? -1;
    const row = group && this.filledRows(group)[index];
    return row ? { group, row } : null;
  }

  private filledRows(group: UntypedFormArray): AbstractControl[] {
    return group.controls.filter((control) => !isEmptyConditionRow(control));
  }

  private filledGroups(): UntypedFormArray[] {
    return this.host.conditionsField.controls.filter(isFilledConditionGroup);
  }

  // no group = start a new one at the end
  private addRule(group: UntypedFormArray | undefined, rule: SavedCondition): void {
    const conditions = this.host.conditionsField;
    const control = buildConditionControl(rule);
    if (this.host.shippingRulesEditor) {
      this.host.shippingRulesEditor.restoreCondition(
        group ? conditions.controls.indexOf(group) : conditions.length,
        control,
      );
    } else if (group) {
      group.push(control);
    } else {
      conditions.push(formBuilder.array([control]));
    }
  }

  private deleteRule(group: UntypedFormArray, row: AbstractControl): void {
    const conditions = this.host.conditionsField;
    const groupIndex = conditions.controls.indexOf(group);
    const rowIndex = group.controls.indexOf(row);
    if (this.host.shippingRulesEditor) {
      this.host.shippingRulesEditor.deleteCondition(groupIndex, rowIndex);
      return;
    }
    group.removeAt(rowIndex);
    if (!group.length) conditions.removeAt(groupIndex);
  }

  // tiers are matched by their sentence, like rules
  private resolvePriceTier(
    entry: ChangeEntry,
    baselineTiers: PriceTier[],
    money: Record<'before' | 'after', Money>,
  ): (() => void) | null {
    const tiers = this.host.additionalPriceRanges;
    if (entry.kind === ChangeKind.Removed) {
      const index = baselineTiers.findIndex((tier) => describePriceTier(tier, money.before) === entry.before);
      if (index === -1) return null;
      return () =>
        tiers.insert(
          Math.min(index, tiers.length),
          buildPriceTierControl(baselineTiers[index] as AdditionalPriceRanges),
        );
    }
    const findIndex = () =>
      tiers.controls.findIndex((tier) => describePriceTier(tier.value, money.after) === entry.after);
    if (entry.kind !== ChangeKind.Added || findIndex() === -1) return null;
    return () => tiers.removeAt(findIndex());
  }

  // area lists live partly outside the form
  private resolveArea(entry: ChangeEntry, tokens: string[]): (() => void) | null {
    const field = tokens[2];
    const item = entry.kind === ChangeKind.Removed ? entry.before : entry.after;

    if (field === 'zones') {
      if (!this.host.zoneOptions?.some((option) => option.label === item)) return null;
      return () => {
        this.host.zoneOptions = this.host.zoneOptions.map((option) =>
          option.label === item ? { ...option, value: entry.kind === ChangeKind.Removed } : option,
        );
        this.host.zonesPlaceholder = this.host.zoneOptions
          .filter((option) => option.value)
          .map((option) => option.label)
          .join(', ');
        this.host.onZonesChange(this.host.zoneOptions);
      };
    }
    if (AREA_LIST_FIELDS.includes(field as AreaListField) && tokens[3] === '[]') {
      const listField = field as AreaListField;
      return () => {
        const list = this.host[listField] ?? [];
        this.host[listField] =
          entry.kind === ChangeKind.Added ? list.filter((value) => value !== item) : [...list, item as string];
      };
    }
    if (field === 'listedStatesControl') {
      return revertAt(this.host.listedStatesControl, tokens.slice(3), entry);
    }
    // the textarea and radios feed polygonInputValues, so revert through them
    if (field === 'zipCodes') {
      return () => {
        this.host.setField('GEODeliveryAreaZipCodes', entry.before ?? '');
        this.host.onZipCodeTextareaFocusOut();
      };
    }
    if (field === 'zipCodesOperator') {
      // the radios always hold a value; an added rule can't be removed
      if (isEmptyValue(entry.before)) return null;
      return () => this.host.onZipCodesOperatorChange(String(entry.before));
    }
    if (field === 'address' || field === 'originZipCode') {
      return () => this.host.setZonesOriginAddress({ ...this.host.zonesOriginAddress, [field]: entry.before });
    }
    return revertAt(this.host.formGroup, tokens, entry);
  }

  private sectionIdByName(name: unknown): string {
    if (name === NO_SECTION) return '';
    const names = this.context.sectionNames();
    return Object.keys(names).find((id) => names[id] === name) ?? '';
  }
}
